/**
 * ZIP Export Utility
 * Handles creation and download of ZIP files
 */

import JSZip from "jszip";

export interface FileToZip {
  name: string;
  content: string | Blob;
}

/**
 * Create and download a ZIP file
 * Uses browser's Blob API and JSZip if available, falls back to simple approach
 */
export async function createAndDownloadZip(
  files: FileToZip[],
  zipName: string,
): Promise<void> {
  try {
    const zip = new JSZip();
    files.forEach((file) => {
      zip.file(file.name, file.content);
    });
    const blob = await zip.generateAsync({ type: "blob" });
    downloadBlob(blob, zipName);
  } catch (error) {
    console.error("Error creating ZIP:", error);
    // Fallback: download individual files
    files.forEach((file) => {
      const blob =
        file.content instanceof Blob
          ? file.content
          : new Blob([file.content], { type: "text/plain;charset=utf-8" });
      downloadBlob(blob, file.name);
    });
  }
}

/**
 * Convert a data URL into a Blob.
 */
export function dataUrlToBlob(dataUrl: string): Blob | null {
  try {
    const [header, data] = dataUrl.split(",");
    if (!header || !data) return null;

    const mimeMatch = header.match(/data:(.*?);base64/);
    const mimeType = mimeMatch?.[1] || "application/octet-stream";
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], { type: mimeType });
  } catch (error) {
    console.error("Failed to convert data URL to Blob:", error);
    return null;
  }
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Create a blob from text content and download
 */
export function downloadText(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  downloadBlob(blob, filename);
}

/**
 * Create a blob from HTML content and download
 */
export function downloadHtml(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/html;charset=utf-8" });
  downloadBlob(blob, filename);
}
